import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import type { AreaDef, TableDef } from '../shared/types';
import { layoutReducer, LayoutAction } from '../shared/layoutReducer';
import { fetchLayout, postLayoutAction, RemoteLayoutState, UnauthorizedError } from '../data/apiClient';
import { useAuth } from './AuthContext';

const POLL_INTERVAL_MS = 5000;

type SetRemoteAction = { type: '__SET_REMOTE'; remote: RemoteLayoutState };

const initialState: RemoteLayoutState = {
  areas: [],
  version: 0,
  updatedAt: 0,
};

function stateReducer(state: RemoteLayoutState, action: LayoutAction | SetRemoteAction): RemoteLayoutState {
  if (action.type === '__SET_REMOTE') {
    return action.remote;
  }
  const next = layoutReducer({ areas: state.areas }, action);
  return { ...state, ...next };
}

interface LayoutContextValue {
  areas: AreaDef[];
  allTables: TableDef[];
  addArea: (areaId: string, name: string, start: number, count: number) => void;
  editArea: (id: string, name: string, start: number, count: number) => void;
  removeArea: (id: string) => void;
  reorderAreas: (orderedIds: string[]) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const versionRef = useRef(state.version);
  versionRef.current = state.version;
  const { logout } = useAuth();

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const remote = await fetchLayout();
        if (!cancelled && remote.version >= versionRef.current) {
          dispatch({ type: '__SET_REMOTE', remote });
        }
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          logout();
        }
      }
    }

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchAction = (action: LayoutAction) => {
    dispatch(action);
    postLayoutAction(action)
      .then((remote) => dispatch({ type: '__SET_REMOTE', remote }))
      .catch(async (err) => {
        if (err instanceof UnauthorizedError) {
          logout();
          return;
        }
        try {
          const remote = await fetchLayout();
          dispatch({ type: '__SET_REMOTE', remote });
        } catch {
          // Still offline — a later poll tick will retry.
        }
      });
  };

  const value = useMemo<LayoutContextValue>(() => {
    const sortedAreas = [...state.areas].sort((a, b) => a.sortOrder - b.sortOrder);
    const allTables: TableDef[] = sortedAreas.flatMap((area) =>
      Array.from({ length: area.count }, (_, i) => ({ num: area.start + i, zoneId: area.id }))
    );
    return {
      areas: sortedAreas,
      allTables,
      addArea: (areaId, name, start, count) =>
        dispatchAction({ type: 'ADD_AREA', areaId, name, start, count }),
      editArea: (id, name, start, count) => dispatchAction({ type: 'EDIT_AREA', id, name, start, count }),
      removeArea: (id) => dispatchAction({ type: 'REMOVE_AREA', id }),
      reorderAreas: (orderedIds) => dispatchAction({ type: 'REORDER_AREAS', orderedIds }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export function useLayout(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayout must be used within a LayoutProvider');
  return ctx;
}
